import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ShieldCheck, Compass, Building2, Layers, Check, X, ArrowRight, AlertCircle } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Overline, PrimaryLink, GhostLink } from "@/components/site";
import { api } from "@/lib/api";
import { PILLARS, FRAMEWORK, PROBLEMS, OUTCOMES, WHY_TWORAY, TRUST_CUES, COMPANY, CONTACT } from "@/data/content";

const HERO_IMG = "https://images.unsplash.com/photo-1690944851207-3f288c8fcd0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const PILLAR_ICONS = { technology: Compass, cyber: ShieldCheck, governance: Building2, transformation: Layers };

export default function Home() {
  const [packages, setPackages] = useState([]);
  useEffect(() => { api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {}); }, []);

  return (
    <div data-testid="page-home">
      <SEO title="Independent Technology & Cyber Security Advisory"
        description="TWORAY Ltd — independent technology and cyber security advisory for startups, scale-ups and growing organisations. Clarity. Security. Growth." />

      {/* HERO */}
      <section className="relative bg-navy text-white min-h-[92vh] flex items-center overflow-hidden grain-overlay">
        <motion.div className="absolute inset-0" initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}>
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy/85" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/50 to-transparent" />
        </motion.div>
        <div className="absolute top-1/4 -left-24 w-[420px] h-[420px] rounded-full bg-brandblue/25 blur-[130px]" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 relative z-10 w-full pt-24">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}><Overline dark>{COMPANY.brandTop}</Overline></motion.div>
          <h1 className="mt-6 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-[4.5rem] leading-[1.02] max-w-4xl">
            <MaskedLines lines={["Clarity for technology.", <span key="s" className="text-cyan">Confidence for growth.</span>]} />
          </h1>
          <motion.p className="mt-7 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}>
            Independent technology and cyber security advisory for startups, scale-ups and growing organisations.
            We help leadership teams understand technology risk, make better decisions and build practical roadmaps for secure, sustainable growth.
          </motion.p>
          <motion.div className="mt-9 flex flex-wrap gap-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}>
            <PrimaryLink to="/book" dark data-testid="hero-cta-button">Book a Discovery Call <ArrowUpRight size={18} className="ml-1.5" /></PrimaryLink>
            <GhostLink to="/packages" dark data-testid="hero-packages-link">Explore Services &amp; Pricing</GhostLink>
          </motion.div>
          <motion.div className="mt-12 flex flex-wrap gap-x-8 gap-y-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}>
            {TRUST_CUES.map((c) => (<span key={c} className="flex items-center gap-2 text-sm text-white/60"><Check size={15} className="text-cyan" /> {c}</span>))}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-ink text-white py-5 border-y border-white/10" data-testid="brand-marquee">
        <Marquee speed={38} gradient={false} autoFill>
          <span className="mx-8 text-2xl sm:text-3xl font-heading font-bold tracking-tight text-white/85">CLARITY <span className="text-cyan mx-3">•</span> SECURITY <span className="text-cyan mx-3">•</span> GROWTH <span className="text-white/20 mx-4">/</span></span>
        </Marquee>
      </section>

      {/* PROBLEMS */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Overline>Problems we solve</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-navy max-w-2xl">The challenges growing organisations bring to us.</h2>
          </Reveal>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <Reveal key={p} delay={i * 0.05}>
                <div className="flex items-start gap-3 bg-lightbg border border-brandborder rounded-md p-6 h-full" data-testid={`problem-${i}`}>
                  <AlertCircle size={20} className="text-brandblue mt-0.5 shrink-0" />
                  <span className="text-navy font-medium">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-16 sm:py-24 bg-lightbg">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Overline>Advisory Pillars</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-navy max-w-2xl">Four disciplines, one independent partner.</h2>
          </Reveal>
          <div className="mt-14 grid gap-px bg-brandborder sm:grid-cols-2 border border-brandborder">
            {PILLARS.map((p, i) => {
              const Icon = PILLAR_ICONS[p.key];
              return (
                <Reveal key={p.key} delay={i * 0.08}>
                  <Link to="/services" data-testid={`pillar-${p.key}`} className="group block bg-white p-8 sm:p-11 h-full hover:bg-lightbg transition-colors duration-300">
                    <div className="flex items-start justify-between">
                      <span className="font-heading font-extrabold text-5xl text-brandborder group-hover:text-cyan transition-colors duration-300">{p.num}</span>
                      <Icon size={28} className="text-brandblue" />
                    </div>
                    <h3 className="mt-8 font-heading font-bold text-2xl text-navy">{p.title}</h3>
                    <p className="mt-3 text-slate leading-relaxed">{p.summary}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODOLOGY FRAMEWORK */}
      <section className="py-24 bg-navy text-white grain-overlay relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 relative z-10">
          <Reveal><Overline dark>How We Work</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl">The TWORAY Advisory Framework.</h2>
          </Reveal>
          <div className="mt-10 flex flex-col items-center text-center">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/50">Discover</div>
            <div className="mt-1 text-sm text-white/55 max-w-md">{FRAMEWORK.discover.desc}</div>
            <div className="my-4 w-px h-6 bg-white/20" />
            <div className="text-xs font-semibold tracking-[0.25em] uppercase text-cyan mb-4">The TWORAY Advisory Framework</div>
            <div className="grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4 border border-white/10 w-full">
              {FRAMEWORK.core.map((s, i) => (
                <Reveal key={s.num} delay={i * 0.06}>
                  <div className="bg-navy p-7 h-full text-left" data-testid={`framework-${s.title.toLowerCase()}`}>
                    <span className="font-heading font-extrabold text-3xl text-cyan">{s.num}</span>
                    <h3 className="mt-2 font-heading font-bold text-xl">{s.title}</h3>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <div className="my-4 w-px h-6 bg-white/20" />
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/50">Assure</div>
            <div className="mt-1 text-sm text-white/55 max-w-md">{FRAMEWORK.assure.desc}</div>
          </div>
          <div className="mt-10 text-center"><GhostLink to="/how-we-work" dark data-testid="home-how-cta">See how TWORAY works</GhostLink></div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <Reveal><Overline>Outcomes</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-navy">Typical engagement outcomes.</h2>
            <p className="mt-4 text-slate leading-relaxed">What organisations typically gain from working with TWORAY. We publish quantified case studies only where we have client permission and verified evidence.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="space-y-4">
              {OUTCOMES.map((o) => (<li key={o} className="flex items-start gap-3" data-testid="outcome-item"><Check size={20} className="text-brandblue mt-0.5 shrink-0" /><span className="text-navy font-medium">{o}</span></li>))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* PACKAGES TEASER */}
      <section className="py-16 sm:py-24 bg-lightbg">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Overline>Packages</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl text-navy">Fixed-scope ways to start.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {packages.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <div className={`rounded-md border p-8 h-full flex flex-col ${p.recommended ? "border-brandblue bg-white shadow-xl" : "border-brandborder bg-white"}`} data-testid={`home-package-${p.slug}`}>
                  <h3 className="font-heading font-bold text-xl text-navy">{p.name}</h3>
                  <p className="mt-1 text-sm text-slate">{p.tagline}</p>
                  <div className="mt-4 font-heading font-extrabold text-4xl text-navy">{p.price}<span className="text-sm font-medium text-slate ml-2">{p.price_note}</span></div>
                  <Link to="/packages" className="mt-6 inline-flex items-center gap-1.5 text-brandblue font-semibold text-sm link-underline pb-0.5 self-start">View details <ArrowRight size={15} /></Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY TWORAY */}
      <section className="py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal><Overline>Why TWORAY</Overline>
            <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-navy max-w-2xl">Boutique by design. Direct by default.</h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {WHY_TWORAY.map((w, i) => (
              <Reveal key={w.num} delay={(i % 3) * 0.06}>
                <div data-testid={`why-${w.num}`}>
                  <span className="font-heading font-extrabold text-3xl text-transparent [-webkit-text-stroke:1px_#0968F0]">{w.num}</span>
                  <h3 className="mt-2 font-heading font-bold text-xl text-navy">{w.title}</h3>
                  <p className="mt-2 text-slate leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOT ANOTHER MSP */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-md border border-brandborder bg-navy text-white p-10 sm:p-14 grain-overlay relative overflow-hidden" data-testid="not-msp">
              <div className="relative z-10">
                <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl">Not another MSP.</h2>
                <p className="mt-4 text-white/75 text-lg leading-relaxed max-w-3xl">
                  TWORAY provides independent technology and cyber security advice focused on business outcomes.
                  We assess, advise and plan before recommending technology or implementation — and we're glad to work
                  alongside your existing IT provider.
                </p>

                <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="msp-comparison">
                  <div className="rounded-md border border-white/10 bg-white/[0.03] p-6">
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-white/50">What traditional MSPs do</div>
                    <ul className="mt-5 space-y-4">
                      {["Sell hardware, licences & software margins",
                        "Reactive helpdesk tickets & billable hours",
                        "Defensive over your existing IT setup"].map((t) => (
                        <li key={t} className="flex items-start gap-3 text-sm text-white/70">
                          <X size={18} className="mt-0.5 shrink-0 text-white/30" /> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="rounded-md border border-cyan/30 bg-cyan/[0.06] p-6">
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan">How TWORAY operates</div>
                    <ul className="mt-5 space-y-4">
                      {["100% independent & vendor-agnostic",
                        "Strategic roadmaps & fixed-price packages",
                        "Collaborates with your existing IT provider"].map((t) => (
                        <li key={t} className="flex items-start gap-3 text-sm font-medium text-white">
                          <Check size={18} className="mt-0.5 shrink-0 text-cyan" /> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-md bg-navy p-10 sm:p-16 text-white grain-overlay">
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-brandblue/25 blur-[120px]" />
              <div className="relative z-10 max-w-2xl">
                <h2 className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl">Ready to get clarity on your technology and risk?</h2>
                <p className="mt-5 text-white/80 text-lg">Book a discovery call. We'll listen, then tell you straight what good looks like for you.</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <PrimaryLink to="/book" dark data-testid="cta-book">Book a Discovery Call</PrimaryLink>
                  <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center justify-center font-semibold text-sm px-6 py-3 rounded-sm border border-white/40 text-white hover:bg-white/10 transition-colors">{CONTACT.email}</a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
